/**
 * Monaco completion provider for SOQL queries.
 *
 * Uses the ANTLR parser to determine context and provide
 * intelligent completions based on Salesforce metadata.
 */

import { getTokensAtPosition, SOQLLexer } from '../../lib/soql-parser/index.js';

/**
 * Calculate Levenshtein distance for fuzzy matching.
 */
function levenshtein(a, b) {
  a = a.toLowerCase();
  b = b.toLowerCase();

  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix = [];
  for (let i = 0; i <= a.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= b.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[a.length][b.length];
}

function capitalize(s) { return s ? s[0].toUpperCase() + s.slice(1) : s; }

/**
 * Build alias-prefixed object completions when dataSources with aliases are present.
 *
 * @param {Array} dataSources - Array of { id, alias, name, sourceType, objects }
 * @param {string} prefix - Current word prefix
 * @param {object} word - Monaco word object
 * @param {object} position - Monaco position
 * @returns {Array} completion items
 */
function getAliasedObjectCompletions(dataSources, prefix, word, position) {
  const suggestions = [];
  if (!dataSources || dataSources.length === 0) return suggestions;

  for (const ds of dataSources) {
    if (!ds.alias) continue;
    const objectNames = ds.objects ? Object.keys(ds.objects) : [];

    for (const objectName of objectNames) {
      const aliasedName = `${ds.alias}.${objectName}`;
      if (prefix) {
        const prefixLower = prefix.toLowerCase();
        // Match if the aliased name starts with the prefix OR the alias itself starts with prefix
        if (!aliasedName.toLowerCase().startsWith(prefixLower) &&
            !ds.alias.toLowerCase().startsWith(prefixLower)) {
          continue;
        }
      }

      suggestions.push({
        label: aliasedName,
        kind: 7, // Class/Object
        detail: `${ds.name} (${ds.sourceType || ds.source_type || 'data source'})`,
        documentation: `Cross-source object: ${ds.alias}.${objectName}`,
        insertText: aliasedName,
        range: {
          startLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endLineNumber: position.lineNumber,
          endColumn: word.endColumn,
        },
      });
    }
  }

  return suggestions;
}

/**
 * Create a completion provider for SOQL.
 *
 * @param {object} metadata - Salesforce metadata object with optional dataSources array
 * @returns {object} Monaco completion provider
 */
export function createSOQLCompletionProvider(metadata) {
  return {
    triggerCharacters: ['.', ' '],

    provideCompletionItems(model, position) {
      const query = model.getValue();
      const offset = model.getOffsetAt(position);
      const word = model.getWordUntilPosition(position);

      // Get token context from ANTLR parser
      const tokenInfo = getTokensAtPosition(query, offset);
      const { context, prefix, previousToken } = tokenInfo;

      // Build completions based on context
      const suggestions = [];

      switch (context) {
        case 'SELECT_FIELDS':
          suggestions.push(...getFieldCompletions(metadata, prefix, word, position));
          suggestions.push(...getAggregateFunctionCompletions(prefix, word, position));
          break;

        case 'FROM_OBJECT':
          suggestions.push(...getObjectCompletions(metadata, prefix, word, position));
          // Also suggest alias.ObjectName when data sources with aliases are present
          if (metadata?.dataSources) {
            suggestions.push(...getAliasedObjectCompletions(metadata.dataSources, prefix, word, position));
          }
          break;

        case 'WHERE_CONDITION':
        case 'HAVING':
          suggestions.push(...getFieldCompletions(metadata, prefix, word, position));
          suggestions.push(...getOperatorCompletions(word, position));
          suggestions.push(...getDateLiteralCompletions(prefix, word, position));
          break;

        case 'GROUP_BY':
        case 'ORDER_BY':
          suggestions.push(...getFieldCompletions(metadata, prefix, word, position));
          if (context === 'ORDER_BY') {
            suggestions.push(...getOrderByKeywords(word, position));
          }
          break;

        case 'AFTER_FROM':
          // After FROM object - could be WHERE, ORDER BY, etc.
          suggestions.push(...getClauseKeywords(word, position));
          break;

        default:
          // General completions
          suggestions.push(...getKeywordCompletions(prefix, word, position));
          break;
      }

      // Handle dot-navigation (relationship traversal)
      if (previousToken && previousToken.text === '.') {
        const relationshipCompletions = getRelationshipCompletions(
          metadata, tokenInfo, word, position
        );
        return { suggestions: relationshipCompletions };
      }

      return { suggestions };
    },
  };
}

/**
 * Get field completions for the main object.
 */
function getFieldCompletions(metadata, prefix, word, position) {
  const suggestions = [];

  // Get current object from metadata (default to first synced object)
  const objectNames = metadata?.objects ? Object.keys(metadata.objects) : [];

  for (const objectName of objectNames) {
    const objMeta = metadata.objects[objectName];
    const fields = objMeta?.fields || [];

    for (const field of fields) {
      const fieldName = field.name || field;
      const label = typeof field === 'string' ? field : field.label || fieldName;

      // Filter by prefix
      if (prefix && !fieldName.toLowerCase().startsWith(prefix.toLowerCase())) {
        // Fuzzy match
        if (levenshtein(prefix, fieldName) > 3) continue;
      }

      suggestions.push({
        label: fieldName,
        kind: 5, // Field
        detail: `${objectName}.${fieldName}`,
        documentation: typeof field === 'object' ? `Type: ${field.type || 'unknown'}` : null,
        insertText: fieldName,
        range: {
          startLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endLineNumber: position.lineNumber,
          endColumn: word.endColumn,
        },
      });
    }
  }

  return suggestions;
}

/**
 * Get object completions for FROM clause.
 */
function getObjectCompletions(metadata, prefix, word, position) {
  const suggestions = [];
  const objectNames = metadata?.objects ? Object.keys(metadata.objects) : [];

  for (const objectName of objectNames) {
    if (prefix && !objectName.toLowerCase().startsWith(prefix.toLowerCase())) {
      if (levenshtein(prefix, objectName) > 3) continue;
    }

    suggestions.push({
      label: objectName,
      kind: 7, // Class/Object
      detail: metadata?.objects?.[objectName]?.sourceType
        ? capitalize(metadata.objects[objectName].sourceType) + ' Object'
        : 'CRM Object',
      insertText: objectName,
      range: {
        startLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: word.endColumn,
      },
    });
  }

  return suggestions;
}

/**
 * Get relationship field completions after a dot.
 */
function getRelationshipCompletions(metadata, tokenInfo, word, position) {
  const suggestions = [];
  const { allTokens, previousToken } = tokenInfo;

  // Find the token before the dot
  let parentObject = null;
  for (let i = allTokens.length - 1; i >= 0; i--) {
    if (allTokens[i] === previousToken && i > 0) {
      const beforeDot = allTokens[i - 1];
      if (beforeDot) {
        parentObject = beforeDot.text;
      }
      break;
    }
  }

  if (!parentObject) return suggestions;

  // Check if parentObject is a relationship name and resolve it
  const lookups = metadata?.lookupRelationships || [];
  let targetObject = parentObject;

  for (const lookup of lookups) {
    if (lookup.relationshipName?.toLowerCase() === parentObject.toLowerCase()) {
      targetObject = lookup.referenceTo;
      break;
    }
  }

  // Get fields for target object
  const objMeta = metadata?.objects?.[targetObject];
  if (objMeta) {
    const fields = objMeta.fields || [];
    for (const field of fields) {
      const fieldName = field.name || field;
      suggestions.push({
        label: fieldName,
        kind: 5,
        detail: `${targetObject}.${fieldName}`,
        insertText: fieldName,
        range: {
          startLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endLineNumber: position.lineNumber,
          endColumn: word.endColumn,
        },
      });
    }

    // Also suggest lookup relationships for deeper traversal
    for (const lookup of lookups) {
      if (lookup.sourceObject === targetObject) {
        suggestions.push({
          label: lookup.relationshipName,
          kind: 18, // Reference
          detail: `→ ${lookup.referenceTo}`,
          insertText: lookup.relationshipName,
          range: {
            startLineNumber: position.lineNumber,
            startColumn: word.startColumn,
            endLineNumber: position.lineNumber,
            endColumn: word.endColumn,
          },
        });
      }
    }
  }

  return suggestions;
}

/**
 * Get aggregate function completions.
 */
function getAggregateFunctionCompletions(prefix, word, position) {
  const functions = ['COUNT', 'SUM', 'AVG', 'MIN', 'MAX'];
  const suggestions = [];

  for (const fn of functions) {
    if (prefix && !fn.toLowerCase().startsWith(prefix.toLowerCase())) continue;

    suggestions.push({
      label: fn,
      kind: 1, // Function
      detail: 'Aggregate function',
      insertText: `${fn}($1)`,
      insertTextRules: 4, // InsertAsSnippet
      range: {
        startLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: word.endColumn,
      },
    });
  }

  return suggestions;
}

/**
 * Get operator completions.
 */
function getOperatorCompletions(word, position) {
  const operators = [
    { label: '=', detail: 'Equals' },
    { label: '!=', detail: 'Not equals' },
    { label: '<', detail: 'Less than' },
    { label: '<=', detail: 'Less than or equal' },
    { label: '>', detail: 'Greater than' },
    { label: '>=', detail: 'Greater than or equal' },
    { label: 'IN', detail: 'In list' },
    { label: 'NOT IN', detail: 'Not in list' },
    { label: 'LIKE', detail: 'Pattern match' },
    { label: 'IS NULL', detail: 'Is null' },
    { label: 'IS NOT NULL', detail: 'Is not null' },
  ];

  return operators.map(op => ({
    label: op.label,
    kind: 11, // Operator
    detail: op.detail,
    insertText: op.label,
    range: {
      startLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endLineNumber: position.lineNumber,
      endColumn: word.endColumn,
    },
  }));
}

/**
 * Get date literal completions.
 */
function getDateLiteralCompletions(prefix, word, position) {
  const dateLiterals = [
    { label: 'TODAY', detail: 'Current date' },
    { label: 'YESTERDAY', detail: 'Previous day' },
    { label: 'TOMORROW', detail: 'Next day' },
    { label: 'THIS_WEEK', detail: 'Current week' },
    { label: 'LAST_WEEK', detail: 'Previous week' },
    { label: 'NEXT_WEEK', detail: 'Next week' },
    { label: 'THIS_MONTH', detail: 'Current month' },
    { label: 'LAST_MONTH', detail: 'Previous month' },
    { label: 'NEXT_MONTH', detail: 'Next month' },
    { label: 'THIS_YEAR', detail: 'Current year' },
    { label: 'LAST_YEAR', detail: 'Previous year' },
    { label: 'NEXT_YEAR', detail: 'Next year' },
    { label: 'LAST_N_DAYS:30', detail: 'Last N days', insertText: 'LAST_N_DAYS:$1' },
    { label: 'NEXT_N_DAYS:30', detail: 'Next N days', insertText: 'NEXT_N_DAYS:$1' },
  ];

  const suggestions = [];
  for (const lit of dateLiterals) {
    if (prefix && !lit.label.toLowerCase().startsWith(prefix.toLowerCase())) continue;

    suggestions.push({
      label: lit.label,
      kind: 20, // Constant
      detail: lit.detail,
      insertText: lit.insertText || lit.label,
      insertTextRules: lit.insertText ? 4 : 0,
      range: {
        startLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: word.endColumn,
      },
    });
  }

  return suggestions;
}

/**
 * Get clause keyword completions.
 */
function getClauseKeywords(word, position) {
  const keywords = ['WHERE', 'GROUP BY', 'ORDER BY', 'LIMIT', 'OFFSET'];

  return keywords.map(kw => ({
    label: kw,
    kind: 14, // Keyword
    insertText: kw,
    range: {
      startLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endLineNumber: position.lineNumber,
      endColumn: word.endColumn,
    },
  }));
}

/**
 * Get ORDER BY modifiers.
 */
function getOrderByKeywords(word, position) {
  const keywords = ['ASC', 'DESC', 'NULLS FIRST', 'NULLS LAST'];

  return keywords.map(kw => ({
    label: kw,
    kind: 14,
    insertText: kw,
    range: {
      startLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endLineNumber: position.lineNumber,
      endColumn: word.endColumn,
    },
  }));
}

/**
 * Get general keyword completions.
 */
function getKeywordCompletions(prefix, word, position) {
  const keywords = [
    'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT',
    'GROUP BY', 'HAVING', 'ORDER BY', 'LIMIT', 'OFFSET',
    'DISTINCT', 'AS',
  ];

  const suggestions = [];
  for (const kw of keywords) {
    if (prefix && !kw.toLowerCase().startsWith(prefix.toLowerCase())) continue;

    suggestions.push({
      label: kw,
      kind: 14,
      insertText: kw,
      range: {
        startLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endLineNumber: position.lineNumber,
        endColumn: word.endColumn,
      },
    });
  }

  return suggestions;
}

export default createSOQLCompletionProvider;
